import { useState } from 'react';
import { useApiData } from '../../../../contexts/apiService';
import Dropdown, { type DropdownOption } from '../../../../components/common/dropdown/dropdown';
import type { DestinationConfig, BswBracket, RFPConfig, ComponentKey, LeaseRow } from '../rfpTypes';
import { COMPONENT_KEYS, COMPONENT_LABELS } from '../rfpTypes';
import { haversineMiles } from '../rfpUtils';
import NumericInput from './NumericInput';

interface DestinationConfigPanelProps {
    config: RFPConfig;
    onChange: (config: RFPConfig) => void;
    leaseRows?: LeaseRow[];
}

const EMPTY_DEST: DestinationConfig = {
    station_owner: '',
    station: '',
    market: '',
    base_deduct: 0,
    bsw_schedule: [],
    is_custom: false,
    component_flags: Object.fromEntries(COMPONENT_KEYS.map(k => [k, false])) as Record<string, boolean>,
    wti_wtl_enabled: false,
    wti_wtl_gravity_threshold: 44,
};

// ── small sub-component for one destination slot ─────────────────────────────
interface DestSlotProps {
    label: string;
    value: DestinationConfig | undefined;
    onChange: (d: DestinationConfig | undefined) => void;
    required?: boolean;
    marketOptions: DropdownOption[];
    /** All unique market_discounts rows keyed by "station_owner||station||market" */
    discountMap: Map<string, { base_deduct: number; bsw_schedule: BswBracket[]; latitude?: number; longitude?: number; component_flags: Record<string, boolean>; wti_wtl_enabled: boolean }>;
}

function buildDestKey(d: DestinationConfig) {
    return `${d.station_owner}||${d.station}||${d.market}`;
}

function DestinationSlot({ label, value, onChange, required, marketOptions, discountMap }: DestSlotProps) {
    const [isCustom, setIsCustom] = useState(value?.is_custom ?? false);
    const enabled = required || value !== undefined;

    const handleEnable = (checked: boolean) => {
        if (required) return; // primary is always enabled
        onChange(checked ? { ...EMPTY_DEST } : undefined);
    };

    const handleSelectPreset = (key: string) => {
        const [station_owner, station, market] = key.split('||');
        const preset = discountMap.get(key) ?? { base_deduct: 0, bsw_schedule: [], component_flags: {}, wti_wtl_enabled: false, latitude: undefined, longitude: undefined };
        onChange({
            station_owner,
            station,
            market,
            base_deduct:               preset.base_deduct,
            bsw_schedule:              preset.bsw_schedule,
            latitude:                  preset.latitude,
            longitude:                 preset.longitude,
            component_flags:           preset.component_flags,
            wti_wtl_enabled:           preset.wti_wtl_enabled,
            wti_wtl_gravity_threshold: value?.wti_wtl_gravity_threshold ?? 44,
            is_custom: false,
        });
    };

    const updateField = <K extends keyof DestinationConfig>(field: K, val: DestinationConfig[K]) => {
        if (!value) return;
        onChange({ ...value, [field]: val });
    };

    const currentKey = value && !isCustom ? buildDestKey(value) : '';

    // Shared sale-side component checkboxes — shown in both preset and custom modes when enabled
    // In preset mode inputs are disabled but visible to indicate the station's formula.
    const ComponentsSection = enabled ? (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Sale Price Components{!isCustom && <span style={{ fontWeight: 400, marginLeft: 4, textTransform: 'none', fontSize: 10 }}>(preset — switch to Custom to edit)</span>}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 5, flexWrap: 'wrap', opacity: isCustom ? 1 : 0.6 }}>
                <input
                    type="checkbox"
                    disabled={!isCustom}
                    checked={value?.wti_wtl_enabled ?? false}
                    onChange={(e) => updateField('wti_wtl_enabled', e.target.checked)}
                />
                <span>WTI / WTL Gravity Split</span>
                {value?.wti_wtl_enabled && (
                    <>
                        <span style={{ color: '#6b7280' }}>≥</span>
                        <input
                            type="number" step="0.1"
                            disabled={!isCustom}
                            value={value?.wti_wtl_gravity_threshold ?? 44}
                            onChange={(e) => updateField('wti_wtl_gravity_threshold', parseFloat(e.target.value) || 44)}
                            style={{ width: 52, padding: '1px 4px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11 }}
                        />
                        <span style={{ color: '#6b7280', fontSize: 10 }}>API → WTL; below → Mid Cush</span>
                    </>
                )}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px' }}>
                {COMPONENT_KEYS.map(k => {
                    const controlled = (k === 'wtl' || k === 'mid_cush_diff') && (value?.wti_wtl_enabled ?? false);
                    const checked = controlled ? true : !!(value?.component_flags?.[k]);
                    return (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: controlled ? '#9ca3af' : '#374151', opacity: isCustom ? 1 : 0.6 }}>
                            <input
                                type="checkbox"
                                disabled={controlled || !isCustom}
                                checked={checked}
                                onChange={(e) => updateField('component_flags', { ...(value?.component_flags ?? {}), [k]: e.target.checked })}
                            />
                            {COMPONENT_LABELS[k as ComponentKey]}
                        </label>
                    );
                })}
            </div>
        </div>
    ) : null;

    return (
        <div style={{ marginBottom: 16, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                {!required && (
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => handleEnable(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                    />
                )}
                <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
                {enabled && (
                    <>
                        <label style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                                type="checkbox"
                                checked={isCustom}
                                onChange={(e) => {
                                    const turningOn = e.target.checked;
                                    setIsCustom(turningOn);
                                    if (!turningOn) {
                                        if (value) onChange({ ...value, is_custom: false });
                                    } else {
                                        onChange({
                                            station_owner:             value?.station_owner             ?? '',
                                            station:                   value?.station                   ?? '',
                                            market:                    value?.market                    ?? '',
                                            base_deduct:               value?.base_deduct               ?? 0,
                                            bsw_schedule:              value?.bsw_schedule              ?? [],
                                            latitude:                  value?.latitude,
                                            longitude:                 value?.longitude,
                                            component_flags:           value?.component_flags           ?? Object.fromEntries(COMPONENT_KEYS.map(k => [k, false])),
                                            wti_wtl_enabled:           value?.wti_wtl_enabled           ?? false,
                                            wti_wtl_gravity_threshold: value?.wti_wtl_gravity_threshold ?? 44,
                                            is_custom:                 true,
                                        });
                                    }
                                }}
                            />
                            Custom
                        </label>
                    </>
                )}
            </div>

            {enabled && !isCustom && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, color: '#374151' }}>Select Destination</label>
                    <Dropdown
                        options={marketOptions}
                        value={currentKey}
                        onChange={handleSelectPreset}
                        placeholder="Select destination…"
                        width={340}
                    />
                    {value && value.station && (
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            Base deduct: <strong>{value.base_deduct?.toFixed(4)}</strong>
                        </div>
                    )}
                    {ComponentsSection}
                </div>
            )}

            {enabled && isCustom && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(
                        [
                            ['station_owner', 'Station Owner'],
                            ['station', 'Station'],
                            ['market', 'Market'],
                        ] as const
                    ).map(([field, lbl]) => (
                        <label key={field} style={{ fontSize: 12, color: '#374151', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {lbl}
                            <input
                                type="text"
                                value={(value?.[field] as string) ?? ''}
                                onChange={(e) => updateField(field, e.target.value)}
                                style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 240 }}
                            />
                        </label>
                    ))}
                    <label style={{ fontSize: 12, color: '#374151', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        Base Deduct ($/bbl)
                        <NumericInput
                            value={value?.base_deduct ?? 0}
                            onChange={(v) => updateField('base_deduct', v)}
                            style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 120 }}
                        />
                    </label>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <label style={{ fontSize: 12, color: '#374151', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            Latitude
                            <input
                                type="number"
                                step="0.000001"
                                placeholder="e.g. 31.8490"
                                value={value?.latitude ?? ''}
                                onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    updateField('latitude', isNaN(v) ? undefined as any : v);
                                }}
                                style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 130 }}
                            />
                        </label>
                        <label style={{ fontSize: 12, color: '#374151', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            Longitude
                            <input
                                type="number"
                                step="0.000001"
                                placeholder="e.g. -104.0290"
                                value={value?.longitude ?? ''}
                                onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    updateField('longitude', isNaN(v) ? undefined as any : v);
                                }}
                                style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, width: 130 }}
                            />
                        </label>
                    </div>
                    {value?.is_custom && (value.latitude == null || value.longitude == null) && (
                        <p style={{ margin: 0, fontSize: 11, color: '#d97706' }}>
                            ⚠ Coordinates required for mileage calculation
                        </p>
                    )}

                    {/* BSW Deduct Schedule */}
                    <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>BS&amp;W Deduct Schedule</span>
                            <button
                                onClick={() => {
                                    const last = (value?.bsw_schedule ?? []).at(-1);
                                    const newBracket: BswBracket = {
                                        bsw_low:     last ? last.bsw_high : 0,
                                        bsw_high:    last ? last.bsw_high + 1 : 1,
                                        discount:    0,
                                        deduct_type: 'fixed',
                                    };
                                    updateField('bsw_schedule', [...(value?.bsw_schedule ?? []), newBracket]);
                                }}
                                style={{ fontSize: 11, padding: '1px 7px', border: '1px solid #3b82f6', borderRadius: 4, background: '#eff6ff', color: '#3b82f6', cursor: 'pointer' }}
                            >
                                + Add
                            </button>
                        </div>
                        {(value?.bsw_schedule ?? []).length === 0 && (
                            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>No brackets — no BS&amp;W deduct applied.</p>
                        )}
                        {(value?.bsw_schedule ?? []).map((bracket, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, fontSize: 11 }}>
                                <input
                                    type="number" step="0.1"
                                    value={bracket.bsw_low}
                                    onChange={(e) => {
                                        const updated = (value!.bsw_schedule).map((b, idx) =>
                                            idx === i ? { ...b, bsw_low: parseFloat(e.target.value) || 0 } : b
                                        );
                                        updateField('bsw_schedule', updated);
                                    }}
                                    style={{ width: 60, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
                                />
                                <span style={{ color: '#6b7280' }}>–</span>
                                <input
                                    type="number" step="0.1"
                                    value={bracket.bsw_high}
                                    onChange={(e) => {
                                        const updated = (value!.bsw_schedule).map((b, idx) =>
                                            idx === i ? { ...b, bsw_high: parseFloat(e.target.value) || 0 } : b
                                        );
                                        updateField('bsw_schedule', updated);
                                    }}
                                    style={{ width: 60, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
                                />
                                <span style={{ color: '#6b7280' }}>%</span>
                                <select
                                    value={bracket.deduct_type ?? 'fixed'}
                                    onChange={(e) => {
                                        const updated = (value!.bsw_schedule).map((b, idx) =>
                                            idx === i ? { ...b, deduct_type: e.target.value as 'fixed' | 'percent' } : b
                                        );
                                        updateField('bsw_schedule', updated);
                                    }}
                                    style={{ padding: '2px 3px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, background: '#fff' }}
                                >
                                    <option value="fixed">Fixed $/bbl</option>
                                    <option value="percent">% of price</option>
                                </select>
                                <NumericInput
                                    value={bracket.discount}
                                    onChange={(v) => {
                                        const updated = (value!.bsw_schedule).map((b, idx) =>
                                            idx === i ? { ...b, discount: v } : b
                                        );
                                        updateField('bsw_schedule', updated);
                                    }}
                                    style={{ width: 72, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' as const }}
                                />
                                <span style={{ color: '#6b7280', minWidth: 28 }}>
                                    {(bracket.deduct_type ?? 'fixed') === 'percent' ? '%' : '$/bbl'}
                                </span>
                                <button
                                    onClick={() => updateField('bsw_schedule', (value!.bsw_schedule).filter((_, idx) => idx !== i))}
                                    style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                    {ComponentsSection}
                </div>
            )}
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function DestinationConfigPanel({ config, onChange, leaseRows = [] }: DestinationConfigPanelProps) {
    const { apiData } = useApiData();

    // Build unique destination options from market_discounts (main=1)
    // joined with market_coordinates for lat/lng
    const { marketOptions, discountMap } = (() => {
        const options: DropdownOption[] = [];
        const map = new Map<string, { base_deduct: number; bsw_schedule: BswBracket[]; latitude?: number; longitude?: number; component_flags: Record<string, boolean>; wti_wtl_enabled: boolean }>();

        // Build station → coords lookup from market_coordinates
        const coordsByStation = new Map<string, { latitude: number; longitude: number }>();
        for (const mc of (apiData?.market_coordinates ?? [])) {
            if (mc.station && !coordsByStation.has(mc.station)) {
                coordsByStation.set(mc.station, {
                    latitude:  Number(mc.latitude),
                    longitude: Number(mc.longitude),
                });
            }
        }

        const rows = (apiData?.market_discounts ?? []).filter(
            (r) => Number(r.main) === 1
        );

        for (const row of rows) {
            const key = `${row.station_owner}||${row.station}||${row.market}`;
            if (!map.has(key)) {
                const base_deduct = parseFloat(String(row.discount)) || 0;
                const coords = coordsByStation.get(row.station);

                // Parse component flags (binary 0/1 columns on main=1 rows)
                const component_flags: Record<string, boolean> = {};
                for (const k of COMPONENT_KEYS) {
                    // DB column for nymex is 'nymex_2/3_1/3'; all others match the key
                    const dbCol = k === 'nymex_2_3_1_3' ? 'nymex_2/3_1/3' : k;
                    component_flags[k] = Number((row as unknown as Record<string, unknown>)[dbCol]) === 1;
                }
                const wti_wtl_enabled = Number((row as unknown as Record<string, unknown>)['wti_wtl_pricing']) === 1;

                options.push({
                    value: key,
                    label: `${row.station} — ${row.market} (${row.station_owner})`,
                });
                map.set(key, { base_deduct, bsw_schedule: [], ...coords, component_flags, wti_wtl_enabled });
            }
        }

        // Pass 2: load BSW brackets (main=0, bsw=1) into each entry
        const bswRows = (apiData?.market_discounts ?? []).filter(
            (r) => Number(r.main) === 0 && Number(r.bsw) === 1
        );
        for (const r of bswRows) {
            const key = `${r.station_owner}||${r.station}||${r.market}`;
            const entry = map.get(key);
            if (entry) {
                entry.bsw_schedule.push({
                    bsw_low:     parseFloat(String(r.bsw_low))  || 0,
                    bsw_high:    parseFloat(String(r.bsw_high)) || 9999,
                    discount:    parseFloat(String(r.discount)) || 0,
                    deduct_type: Number(r.disc_percentage) === 1 ? 'percent' : 'fixed',
                });
            }
        }
        for (const entry of Array.from(map.values())) {
            entry.bsw_schedule.sort((a: BswBracket, b: BswBracket) => a.bsw_low - b.bsw_low);
        }

        options.sort((a, b) => a.label.localeCompare(b.label));

        return { marketOptions: options, discountMap: map };
    })();

    // Carrier options from trucking_rates column names
    const carrierOptions: DropdownOption[] = (() => {
        const firstRow = apiData?.trucking_rates?.[0];
        if (!firstRow) return [];
        return Object.keys(firstRow)
            .filter((k) => k !== 'id' && k !== 'mileage' && k !== 'uuid')
            .map((k) => ({
                value: k,
                label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            }));
    })();

    // ── Closest-market finder state ─────────────────────────────────────────
    const [suggestions, setSuggestions] = useState<{ key: string; label: string; miles: number; base_deduct: number }[] | null>(null);

    const findClosestMarkets = () => {
        const validLeases = leaseRows.filter(l => l.latitude && l.longitude);
        if (!validLeases.length) return;
        const centLat = validLeases.reduce((s, l) => s + l.latitude, 0) / validLeases.length;
        const centLng = validLeases.reduce((s, l) => s + l.longitude, 0) / validLeases.length;

        const ranked = Array.from(discountMap.entries())
            .filter(([, v]) => v.latitude != null && v.longitude != null)
            .map(([key, v]) => ({
                key,
                label: marketOptions.find(o => o.value === key)?.label ?? key,
                miles: Math.round(haversineMiles(centLat, centLng, v.latitude!, v.longitude!)),
                base_deduct: v.base_deduct,
            }))
            .sort((a, b) => a.miles - b.miles)
            .slice(0, 3);

        setSuggestions(ranked);
    };

    const handleSuggestionPick = (key: string) => {
        const [station_owner, station, market] = key.split('||');
        const preset = discountMap.get(key)!;
        onChange({
            ...config,
            primary_destination: {
                station_owner,
                station,
                market,
                base_deduct:               preset.base_deduct,
                bsw_schedule:              preset.bsw_schedule,
                latitude:                  preset.latitude,
                longitude:                 preset.longitude,
                component_flags:           preset.component_flags,
                wti_wtl_enabled:           preset.wti_wtl_enabled,
                wti_wtl_gravity_threshold: config.primary_destination.wti_wtl_gravity_threshold ?? 44,
                is_custom: false,
            },
        });
        setSuggestions(null);
    };

    return (
        <div>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Destinations & Carrier</h4>

            {/* BSW Threshold — only relevant when a waste destination is configured */}
            {config.waste_destination !== undefined && (
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <span style={{ fontWeight: 600 }}>BS&W Threshold (%)</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        (leases above this go to Waste destination)
                    </span>
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.bsw_threshold}
                        onChange={(e) =>
                            onChange({ ...config, bsw_threshold: parseFloat(e.target.value) || 0 })
                        }
                        style={{ padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, width: 80, fontSize: 13 }}
                    />
                </label>
            )}

            {/* Closest-market finder */}
            {leaseRows.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                    <button
                        onClick={findClosestMarkets}
                        style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #6366f1', borderRadius: 5, background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontWeight: 600 }}
                    >
                        🎯 Find Closest Markets
                    </button>
                    {suggestions && (
                        <div style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #c7d2fe', borderRadius: 6, background: '#f5f3ff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                    Closest markets to lease centroid
                                </span>
                                <button onClick={() => setSuggestions(null)} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕</button>
                            </div>
                            {suggestions.length === 0 && (
                                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>No markets with coordinates found.</p>
                            )}
                            {suggestions.map(s => (
                                <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '5px 6px', borderRadius: 4, marginBottom: 4, background: '#fff', border: '1px solid #e0e7ff' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
                                        <div style={{ fontSize: 11, color: '#6b7280' }}>~{s.miles.toLocaleString()} mi &nbsp;·&nbsp; base deduct: {s.base_deduct.toFixed(4)}</div>
                                    </div>
                                    <button
                                        onClick={() => handleSuggestionPick(s.key)}
                                        style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #6366f1', borderRadius: 4, background: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}
                                    >
                                        Select
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <DestinationSlot
                label="Primary Destination"
                value={config.primary_destination}
                onChange={(d) => onChange({ ...config, primary_destination: d ?? EMPTY_DEST })}
                required
                marketOptions={marketOptions}
                discountMap={discountMap}
            />

            <DestinationSlot
                label="Secondary Destination"
                value={config.secondary_destination}
                onChange={(d) => onChange({ ...config, secondary_destination: d })}
                marketOptions={marketOptions}
                discountMap={discountMap}
            />

            <DestinationSlot
                label="Waste Destination"
                value={config.waste_destination}
                onChange={(d) => onChange({ ...config, waste_destination: d })}
                marketOptions={marketOptions}
                discountMap={discountMap}
            />

            {/* Carrier */}
            <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Carrier</span>
                <div style={{ marginTop: 6 }}>
                    <Dropdown
                        options={carrierOptions}
                        value={config.carrier}
                        onChange={(v) => onChange({ ...config, carrier: v })}
                        placeholder="Select carrier…"
                        width={240}
                    />
                </div>
            </div>
        </div>
    );
}
